import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { InboxItem } from '../types';
import { showToast } from '../utils/toast';

type DbInboxItem = Database['public']['Tables']['inbox_items']['Row'];
type DbInboxItemInsert = Database['public']['Tables']['inbox_items']['Insert'];
type DbInboxItemUpdate = Database['public']['Tables']['inbox_items']['Update'];

const mapToInboxItem = (dbItem: DbInboxItem): InboxItem => ({
  id: dbItem.id,
  content: dbItem.content,
  createdAt: dbItem.created_at || new Date().toISOString(),
  // AI-020: audio fields (present only when item_type === 'audio')
  itemType: (dbItem.item_type as 'text' | 'audio') || 'text',
  audioUrl: dbItem.audio_url ?? undefined,
  audioDurationS: dbItem.audio_duration_s ?? undefined,
  // AI-021: transcription fields
  transcriptText: dbItem.transcript_text ?? undefined,
  transcriptStatus: (dbItem.transcript_status as import('../types').TranscriptStatus) || 'none',
  extractionJson: dbItem.extraction_json ?? undefined,
  canvasJson: dbItem.canvas_json ?? undefined,
  synapseStatus: (dbItem.synapse_status as import('../types').SynapseStatus) || 'recorded',
});

export const inboxService = {
  async getInboxItems(): Promise<InboxItem[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      // Fetch only pending items. Items transformed to tasks/notes are deleted or marked processed.
      const { data, error } = await supabase
        .from('inbox_items')
        .select('*')
        .neq('status', 'deleted')
        .neq('status', 'processed')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[inbox.service] getInboxItems:', error.message);
        showToast('Erreur lors du chargement de la boîte de réception', 'error');
        return [];
      }
      return (data || []).map(mapToInboxItem);
    } catch (err: any) {
      console.error('[inbox.service] getInboxItems:', err.message);
      showToast('Erreur lors du chargement de la boîte de réception', 'error');
      return [];
    }
  },

  async createInboxItem(item: Partial<InboxItem>): Promise<InboxItem> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const insertData: DbInboxItemInsert = {
      user_id: user.id,
      content: item.content || '',
      status: 'pending' as Database['public']['Enums']['inbox_status'],
      // AI-020: audio fields
      item_type: item.itemType ?? 'text',
      audio_url: item.audioUrl ?? null,
      audio_duration_s: item.audioDurationS ?? null,
    };

    const { data, error } = await supabase.from('inbox_items').insert(insertData).select().single();

    if (error) {
      console.error('[inbox.service] createInboxItem:', error.message);
      showToast('Impossible de créer l’élément', 'error');
      throw error;
    }
    return mapToInboxItem(data);
  },

  async updateInboxItem(id: string, updates: Partial<InboxItem>): Promise<InboxItem> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const updateData: DbInboxItemUpdate = {
      ...(updates.content !== undefined && { content: updates.content }),
      // AI-021: allow updating transcript fields (e.g. user edits the transcript text)
      ...(updates.transcriptText !== undefined && { transcript_text: updates.transcriptText }),
      ...(updates.transcriptStatus !== undefined && {
        transcript_status: updates.transcriptStatus,
      }),
      ...(updates.extractionJson !== undefined && { extraction_json: updates.extractionJson }),
      ...(updates.canvasJson !== undefined && { canvas_json: updates.canvasJson }),
      ...(updates.synapseStatus !== undefined && { synapse_status: updates.synapseStatus }),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('inbox_items')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[inbox.service] updateInboxItem:', error.message);
      showToast('Impossible de mettre à jour l’élément', 'error');
      throw error;
    }
    return mapToInboxItem(data);
  },

  async deleteInboxItem(id: string, audioUrl?: string): Promise<void> {
    // During transformation, we can either legally delete the item or mark it as 'processed'.
    // For safety and traceabiltiy, we will execute a physical delete to match other modules,
    // or if you prefer keeping history, marking it as processed :
    const { error } = await supabase.from('inbox_items').delete().eq('id', id);

    if (error) {
      console.error('[inbox.service] deleteInboxItem:', error.message);
      showToast("Impossible de supprimer l'élément", 'error');
      throw error;
    }

    // Non-blocking: clean up the associated audio file from Storage if present.
    if (audioUrl) {
      supabase.storage
        .from('audio-notes')
        .remove([audioUrl])
        .then(({ error: storageError }) => {
          if (storageError) {
            console.error(
              '[inbox.service] deleteInboxItem – storage cleanup failed:',
              storageError.message
            );
          }
        });
    }
  },
};
